import { z } from 'zod';
import { whereField } from './fields/index.js';
import { DATE_UNITS, type WhereOperator, operatorsFor } from './operators.js';
import {
  type WhereDataset,
  type WhereField,
  type WhereGroup,
  type WhereRule,
  isWhereGroup,
} from './types.js';

/** `encoded`: the condition's JSON, URL-encoded, so any valid one fits a 16 KB table link and GET `where`. */
export const WHERE_LIMITS = {
  depth: 3,
  rules: 40,
  values: 100,
  text: 200,
  encoded: 12_000,
} as const;

export type WhereErrorCode =
  | 'unknown_field'
  | 'bad_operator'
  | 'bad_value'
  | 'too_deep'
  | 'too_many_rules'
  | 'too_long'
  | 'bad_shape';

export class WhereError extends Error {
  constructor(
    readonly code: WhereErrorCode,
    readonly path: string,
    message: string,
  ) {
    super(message);
    this.name = 'WhereError';
  }
}

const text = z
  .string()
  .trim()
  .min(1)
  .max(WHERE_LIMITS.text, `a value holds at most ${WHERE_LIMITS.text} characters`);
const textList = z
  .array(text)
  .min(1)
  .max(WHERE_LIMITS.values, `at most ${WHERE_LIMITS.values} values`)
  .transform((v) => [...new Set(v)]);
const viewIds = z
  .array(z.string().uuid('a saved View is named by its id'))
  .min(1)
  .max(WHERE_LIMITS.values, `at most ${WHERE_LIMITS.values} values`)
  .transform((v) => [...new Set(v)]);
const isoDate = z.string().date();
const num = z.number().finite();
const numPair = z
  .tuple([num, num])
  .refine(([a, b]) => a <= b, 'the first number must not be above the second');
const scoreNum = num.min(0, 'scores run 0 to 100').max(100, 'scores run 0 to 100');
const scorePair = z
  .tuple([scoreNum, scoreNum])
  .refine(([a, b]) => a <= b, 'the first number must not be above the second');
const datePair = z
  .tuple([isoDate, isoDate])
  .refine(([a, b]) => a <= b, 'the first date must not be after the second');
const ago = z
  .object({ amount: z.number().int().min(1).max(3650), unit: z.enum(DATE_UNITS) })
  .strict();

function valueSchema(field: WhereField, op: WhereOperator): z.ZodTypeAny | null {
  if (op === 'null' || op === 'notNull') return null;
  if (field.key === 'view') return viewIds;
  switch (field.kind) {
    case 'text':
      return op === 'in' || op === 'notIn' ? textList : text;
    case 'choice': {
      if (!field.options?.length) return textList;
      const allowed = new Set(field.options.map((o) => o.value));
      return textList.superRefine((values, ctx) => {
        const bad = values.find((v) => !allowed.has(v));
        if (bad !== undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `"${bad}" is not one of ${[...allowed].join(', ')}`,
          });
        }
      });
    }
    case 'number':
      return op === 'between' ? numPair : num;
    case 'score':
      return op === 'between' ? scorePair : scoreNum;
    case 'date':
      return op === 'between' ? datePair : ago;
    case 'yes_no':
      return z.boolean();
  }
}

function fail(code: WhereErrorCode, path: string, message: string): never {
  throw new WhereError(code, path, message);
}

function parseRule(dataset: WhereDataset, raw: Record<string, unknown>, path: string): WhereRule {
  const key = raw.field;
  if (typeof key !== 'string')
    fail('bad_shape', `${path}.field`, `${path}.field must be a field key.`);
  const field = whereField(dataset, key);
  if (!field) fail('unknown_field', `${path}.field`, `Unknown field "${key}" for ${dataset}s.`);
  const allowed = operatorsFor(field);
  const op = raw.operator as WhereOperator;
  if (!allowed.includes(op)) {
    fail(
      'bad_operator',
      `${path}.operator`,
      `${key} takes ${allowed.join(', ')}; got "${String(raw.operator)}".`,
    );
  }
  const schema = valueSchema(field, op);
  if (!schema) return { field: key, operator: op };
  const parsed = schema.safeParse(raw.value);
  if (!parsed.success) {
    fail(
      'bad_value',
      `${path}.value`,
      `${key} ${op}: ${parsed.error.issues[0]?.message ?? 'invalid value'}.`,
    );
  }
  return { field: key, operator: op, value: parsed.data };
}

function parseGroup(
  dataset: WhereDataset,
  raw: unknown,
  path: string,
  depth: number,
  count: { n: number },
): WhereGroup {
  const label = path || 'where';
  if (depth > WHERE_LIMITS.depth)
    fail('too_deep', path, `Groups nest at most ${WHERE_LIMITS.depth} deep.`);
  if (!raw || typeof raw !== 'object' || !Array.isArray((raw as WhereGroup).rules)) {
    fail('bad_shape', path, `${label} must be {combinator, rules}.`);
  }
  const group = raw as WhereGroup;
  const combinator = group.combinator ?? 'and';
  if (combinator !== 'and' && combinator !== 'or') {
    fail('bad_shape', path ? `${path}.combinator` : 'combinator', 'combinator is "and" or "or".');
  }
  if (group.not !== undefined && typeof group.not !== 'boolean') {
    fail('bad_shape', path ? `${path}.not` : 'not', 'not is true or false.');
  }
  const rules = group.rules.map((node, i) => {
    const at = path ? `${path}.rules.${i}` : `rules.${i}`;
    if (!node || typeof node !== 'object' || Array.isArray(node)) {
      fail('bad_shape', at, `${at} must be a rule {field, operator, value} or a group.`);
    }
    if (Array.isArray((node as WhereGroup).rules)) {
      return parseGroup(dataset, node, at, depth + 1, count);
    }
    count.n += 1;
    if (count.n > WHERE_LIMITS.rules) {
      fail('too_many_rules', at, `A condition holds at most ${WHERE_LIMITS.rules} rules.`);
    }
    return parseRule(dataset, node as unknown as Record<string, unknown>, at);
  });
  return group.not ? { combinator, not: true, rules } : { combinator, rules };
}

/** Validates a condition for one dataset and returns a normalised copy (ids stripped, values trimmed). */
export function parseWhere(dataset: WhereDataset, raw: unknown): WhereGroup {
  const where = parseGroup(dataset, raw, '', 1, { n: 0 });
  if (encodeURIComponent(JSON.stringify(where)).length > WHERE_LIMITS.encoded) {
    throw new WhereError(
      'too_long',
      '',
      `A condition is at most ${WHERE_LIMITS.encoded.toLocaleString('en-US')} characters once URL-encoded; use fewer values.`,
    );
  }
  return where;
}

export function safeParseWhere(
  dataset: WhereDataset,
  raw: unknown,
): { ok: true; where: WhereGroup } | { ok: false; error: WhereError } {
  try {
    return { ok: true, where: parseWhere(dataset, raw) };
  } catch (error) {
    if (error instanceof WhereError) return { ok: false, error };
    throw error;
  }
}

export function whereRules(where: WhereGroup): WhereRule[] {
  return where.rules.flatMap((node) => (isWhereGroup(node) ? whereRules(node) : [node]));
}

export function whereViewIds(where: WhereGroup): string[] {
  const ids = whereRules(where)
    .filter((r) => r.field === 'view')
    .flatMap((r) => (Array.isArray(r.value) ? r.value : []));
  return [...new Set(ids as string[])];
}

/** The datasets whose score this condition reads: its own for `score`, accounts for `firm.score`. */
export function whereScoreDatasets(dataset: WhereDataset, where: WhereGroup): WhereDataset[] {
  const out = new Set<WhereDataset>();
  for (const r of whereRules(where)) {
    if (r.field === 'score') out.add(dataset);
    if (r.field === 'firm.score') out.add('account');
  }
  return [...out];
}
